import { useMemo, useState, useRef, useEffect } from 'react';
import { tasksToTimeline, formatDateOnly } from '../utils/calendarUtils.js';
import { Badge, Empty, PRIORITY_TONE, STATUS_TONE, label } from './ui.jsx';
import { t, getLang } from '../i18n/index.jsx';

const MS_DAY = 86400000;
const ZOOMS = [
  { id: 'fit', label: 'Fit' },
  { id: '30', label: '30d', days: 30 },
  { id: '90', label: '90d', days: 90 },
];

function fmtShort(d) {
  return d.toLocaleDateString(getLang() === 'it' ? 'it-IT' : 'en-GB', { day: '2-digit', month: 'short' });
}

/**
 * Lightweight project timeline with zoom, today marker, collapsible groups.
 */
export default function TaskTimeline({ tasks, onTaskClick }) {
  const rows = useMemo(() => tasksToTimeline(tasks), [tasks]);
  const [zoom, setZoom] = useState('fit');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const trackScrollRef = useRef(null);

  const { rangeStart, spanDays, groups, todayPct } = useMemo(() => {
    if (!rows.length) {
      return { rangeStart: new Date(), spanDays: 1, groups: [], todayPct: null };
    }
    let min = rows[0].start;
    let max = rows[0].end;
    rows.forEach((r) => {
      if (r.start < min) min = r.start;
      if (r.end > max) max = r.end;
    });

    const zoomDef = ZOOMS.find((z) => z.id === zoom);
    let start;
    let end;
    if (zoomDef?.days) {
      // Center on "today" (or mid of data if far away)
      const mid = new Date();
      start = new Date(mid); start.setDate(start.getDate() - Math.floor(zoomDef.days / 3));
      end = new Date(start); end.setDate(end.getDate() + zoomDef.days);
      // ensure data is visible if entirely outside window
      if (max < start || min > end) {
        start = new Date(min); start.setDate(start.getDate() - 2);
        end = new Date(start); end.setDate(end.getDate() + zoomDef.days);
      }
    } else {
      start = new Date(min); start.setDate(start.getDate() - 3);
      end = new Date(max); end.setDate(end.getDate() + 3);
    }

    const span = Math.max(1, Math.round((end - start) / MS_DAY));
    const byProj = new Map();
    rows.forEach((r) => {
      const key = r.projectId;
      if (!byProj.has(key)) byProj.set(key, { id: key, name: r.projectName, items: [] });
      byProj.get(key).items.push(r);
    });

    const today = new Date(); today.setHours(0, 0, 0, 0);
    let tPct = null;
    if (today >= start && today <= end) {
      tPct = ((today - start) / MS_DAY / span) * 100;
    }

    return {
      rangeStart: start,
      spanDays: span,
      groups: [...byProj.values()],
      todayPct: tPct,
    };
  }, [rows, zoom]);

  const ticks = useMemo(() => {
    const step = spanDays > 120 ? 14 : spanDays > 50 ? 7 : spanDays > 20 ? 3 : 1;
    const out = [];
    for (let i = 0; i <= spanDays; i += step) {
      const d = new Date(rangeStart);
      d.setDate(d.getDate() + i);
      out.push({ pct: (i / spanDays) * 100, label: fmtShort(d), key: i });
    }
    return out;
  }, [rangeStart, spanDays]);

  useEffect(() => {
    // Scroll track so today is roughly centered when possible
    if (todayPct == null || !trackScrollRef.current) return;
    const el = trackScrollRef.current;
    const target = (todayPct / 100) * el.scrollWidth - el.clientWidth / 2;
    el.scrollLeft = Math.max(0, target);
  }, [todayPct, zoom]);

  if (!rows.length) {
    return (
      <Empty>{t('No dated tasks to show on the timeline. Add start and due dates to tasks.')}</Empty>
    );
  }

  function barStyle(row) {
    const left = (row.start - rangeStart) / MS_DAY;
    const width = Math.max(1, Math.round((row.end - row.start) / MS_DAY) + 1);
    return {
      left: `${(left / spanDays) * 100}%`,
      width: `${(width / spanDays) * 100}%`,
    };
  }

  function toggleGroup(id) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="timeline panel">
      <div className="timeline-toolbar">
        <span className="muted sm">{t('{n} dated tasks', { n: rows.length })}</span>
        <div className="sheet-tabs" style={{ margin: 0 }}>
          {ZOOMS.map((z) => (
            <button
              key={z.id}
              type="button"
              className={`chip ${zoom === z.id ? 'on' : ''}`}
              onClick={() => setZoom(z.id)}
            >
              {z.id === 'fit' ? t('Fit') : z.label}
            </button>
          ))}
        </div>
      </div>

      <div className="timeline-scroll" ref={trackScrollRef}>
        <div className="timeline-inner" style={{ minWidth: Math.max(720, spanDays * (zoom === 'fit' ? 10 : 18)) }}>
          <div className="timeline-head">
            <div className="timeline-label-col muted sm">{t('Task')}</div>
            <div className="timeline-track-col">
              <div className="timeline-ruler">
                {ticks.map((tk) => (
                  <span key={tk.key} className="timeline-tick" style={{ left: `${tk.pct}%` }}>{tk.label}</span>
                ))}
                {todayPct != null && (
                  <span className="timeline-today-line" style={{ left: `${todayPct}%` }} title={t('Today')} />
                )}
              </div>
            </div>
          </div>

          {groups.map((g) => {
            const isCollapsed = collapsed.has(g.id);
            return (
              <div key={g.id} className="timeline-group">
                <button
                  type="button"
                  className="timeline-group-title"
                  onClick={() => toggleGroup(g.id)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="timeline-chevron">{isCollapsed ? '▸' : '▾'}</span>
                  {g.name}
                  <span className="muted sm">({g.items.length})</span>
                </button>
                {!isCollapsed && g.items.map((row) => {
                  const style = barStyle(row);
                  const offRange = parseFloat(style.left) + parseFloat(style.width) < 0
                    || parseFloat(style.left) > 100;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className="timeline-row"
                      onClick={() => onTaskClick?.(row.raw)}
                    >
                      <div className="timeline-label-col">
                        <div className="timeline-task-name" title={row.title}>{row.title}</div>
                        <div className="timeline-meta">
                          <Badge tone={PRIORITY_TONE[row.priority] || 'gray'}>{label(row.priority)}</Badge>
                          <Badge tone={STATUS_TONE[row.status] || 'gray'}>{label(row.status)}</Badge>
                        </div>
                      </div>
                      <div className="timeline-track-col">
                        <div className="timeline-track">
                          {todayPct != null && (
                            <span className="timeline-today-line soft" style={{ left: `${todayPct}%` }} />
                          )}
                          {!offRange && (
                            <div
                              className={`timeline-bar status-${row.status || 'todo'}`}
                              style={style}
                              title={`${row.title}\n${formatDateOnly(row.start)} → ${formatDateOnly(row.end)}`}
                            >
                              <span className="timeline-bar-fill" style={{ width: `${row.progress}%` }} />
                              <span className="timeline-bar-label">
                                {fmtShort(row.start)} – {fmtShort(row.end)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
